import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ClientMessage,
  ErrorCode,
  PublicMove,
  PublicRoomState,
  Role,
  ServerMessage,
} from '../../shared/protocol.ts';
import { loadSeatToken, resolveSeatToken, saveSeatToken } from './api.ts';

export type ConnectionPhase = 'connecting' | 'open' | 'reconnecting' | 'not_found' | 'closed';

const CLOSE_NOT_FOUND = 4004;
const CLOSE_ROOM_CLOSED = 4000;
const HEARTBEAT_MS = 20_000;
const MAX_BACKOFF_MS = 8_000;

export interface OnlineRoom {
  phase: ConnectionPhase;
  role: Role | null;
  state: PublicRoomState | null;
  /** Client time (Date.now) when `state` arrived; clocks count down from here. */
  receivedAt: number;
  /** Server moves plus an optimistic move awaiting confirmation. */
  moves: PublicMove[];
  lastError: { code: ErrorCode; at: number } | null;
  send: (message: ClientMessage) => void;
  move: (move: PublicMove) => void;
}

/** Real-time connection to a room's Durable Object with automatic reconnection. */
export function useOnlineRoom(code: string, name: string): OnlineRoom {
  const [phase, setPhase] = useState<ConnectionPhase>('connecting');
  const [role, setRole] = useState<Role | null>(null);
  const [snapshot, setSnapshot] = useState<{ state: PublicRoomState; receivedAt: number } | null>(null);
  const [pending, setPending] = useState<PublicMove | null>(null);
  const [lastError, setLastError] = useState<OnlineRoom['lastError']>(null);

  const socketRef = useRef<WebSocket | null>(null);
  const nameRef = useRef(name);
  useEffect(() => {
    nameRef.current = name;
  }, [name]);

  useEffect(() => {
    let disposed = false;
    let attempt = 0;
    let retryTimer: number | undefined;
    let heartbeat: number | undefined;
    let terminal = false;

    const connect = () => {
      if (disposed || terminal) return;
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const ws = new WebSocket(`${protocol}://${window.location.host}/api/rooms/${code}/ws`);
      socketRef.current = ws;

      ws.onopen = () => {
        attempt = 0;
        ws.send(JSON.stringify({ type: 'hello', token: loadSeatToken(code), name: nameRef.current } satisfies ClientMessage));
        heartbeat = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, HEARTBEAT_MS);
      };

      ws.onmessage = (event) => {
        if (event.data === 'pong') return;
        let msg: ServerMessage;
        try {
          msg = JSON.parse(event.data as string) as ServerMessage;
        } catch {
          return;
        }
        if (msg.type === 'welcome') {
          if (msg.token) saveSeatToken(code, msg.token);
          setRole(msg.role);
          setSnapshot({ state: msg.state, receivedAt: Date.now() });
          setPending(null);
          setPhase('open');
        } else if (msg.type === 'state') {
          setRole(msg.role);
          setSnapshot({ state: msg.state, receivedAt: Date.now() });
          setPending(null);
        } else if (msg.type === 'error') {
          if (msg.code === 'not_found') {
            terminal = true;
            setPhase('not_found');
          } else {
            setPending(null);
            setLastError({ code: msg.code, at: Date.now() });
          }
        }
      };

      ws.onclose = (event) => {
        window.clearInterval(heartbeat);
        if (socketRef.current === ws) socketRef.current = null;
        if (disposed) return;
        if (event.code === CLOSE_NOT_FOUND) {
          terminal = true;
          setPhase('not_found');
          return;
        }
        if (event.code === CLOSE_ROOM_CLOSED) {
          terminal = true;
          setPhase('closed');
          return;
        }
        if (terminal) return;
        setPhase((p) => (p === 'connecting' ? 'connecting' : 'reconnecting'));
        const delay = Math.min(MAX_BACKOFF_MS, 500 * 2 ** attempt) * (0.75 + Math.random() * 0.5);
        attempt += 1;
        retryTimer = window.setTimeout(connect, delay);
      };
    };

    // Reconnect immediately when the tab becomes visible or the network returns.
    const wake = () => {
      if (!ready || document.visibilityState !== 'visible' || socketRef.current || terminal) return;
      window.clearTimeout(retryTimer);
      attempt = 0;
      connect();
    };

    // Pick the seat first (this tab's, or one this browser left behind), then connect.
    let ready = false;
    void resolveSeatToken(code).then(() => {
      ready = true;
      if (!disposed && !socketRef.current) connect();
    });
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('online', wake);

    return () => {
      disposed = true;
      window.clearTimeout(retryTimer);
      window.clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('online', wake);
      socketRef.current?.close(1000, 'leaving');
      socketRef.current = null;
    };
  }, [code]);

  const send = useCallback((message: ClientMessage) => {
    const ws = socketRef.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(message));
  }, []);

  const move = useCallback(
    (m: PublicMove) => {
      setPending(m);
      send({ type: 'move', from: m.from, to: m.to, ...(m.promotion ? { promotion: m.promotion } : {}) });
    },
    [send],
  );

  const state = snapshot?.state ?? null;
  const moves = state ? (pending ? [...state.moves, pending] : state.moves) : [];

  return { phase, role, state, receivedAt: snapshot?.receivedAt ?? 0, moves, lastError, send, move };
}
